<script setup>
import { ref, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import { Avatar, AvatarFallback, AvatarImage } from '@/Components/ui/avatar'
import { Badge } from '@/Components/ui/badge'
import { Search, X, UserRoundCog } from 'lucide-vue-next'
import { useScrollLock } from '@/composables/useScrollLock'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])

useScrollLock(() => props.show)

const searchQuery = ref('')
const userTypeFilter = ref('')
const users = ref([])
const loading = ref(false)
const impersonating = ref(false)

let debounceTimer = null

const userTypes = [
  { value: '', label: 'All Users' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'student', label: 'Student' },
  { value: 'system', label: 'System' },
  { value: 'limited-system', label: 'Limited System' },
]

const fetchUsers = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams()
    if (searchQuery.value) params.append('search', searchQuery.value)
    if (userTypeFilter.value) params.append('user_type', userTypeFilter.value)

    const response = await fetch(`/impersonate/search?${params.toString()}`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
    const data = await response.json()
    users.value = data.users
  } catch (error) {
    console.error('Failed to fetch users:', error)
  } finally {
    loading.value = false
  }
}

const debouncedFetch = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(fetchUsers, 300)
}

watch(() => props.show, (newVal) => {
  if (newVal) {
    searchQuery.value = ''
    userTypeFilter.value = ''
    users.value = []
    fetchUsers()
  }
})

watch(searchQuery, debouncedFetch)
watch(userTypeFilter, fetchUsers)

const impersonate = (user) => {
  if (impersonating.value) return
  impersonating.value = true
  router.post(`/impersonate/${user.id}/start`)
}

const getTypeColor = (type) => {
  const colors = {
    teacher: 'bg-blue-100 text-blue-800',
    student: 'bg-green-100 text-green-800',
    system: 'bg-purple-100 text-purple-800',
    'limited-system': 'bg-orange-100 text-orange-800',
  }
  return colors[type] || 'bg-gray-100 text-gray-800'
}

const getTypeLabel = (type) => {
  const labels = {
    teacher: 'Teacher',
    student: 'Student',
    system: 'System',
    'limited-system': 'Limited System',
  }
  return labels[type] || type
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
        <!-- Backdrop -->
        <div
          class="fixed inset-0 bg-black/50 transition-opacity"
          @click="emit('close')"
        ></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div
            class="relative bg-white rounded-2xl shadow-xl max-w-lg w-full transform transition-all"
            @click.stop
          >
            <!-- Header -->
            <div class="border-b border-gray-200 px-6 py-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <UserRoundCog class="h-5 w-5 text-amber-600" />
                  <h3 class="text-lg font-bold text-gray-900">Impersonate User</h3>
                </div>
                <button
                  type="button"
                  @click="emit('close')"
                  class="text-gray-400 hover:text-gray-500 transition-colors"
                >
                  <X class="h-5 w-5" />
                </button>
              </div>
            </div>

            <!-- Search & Filter -->
            <div class="px-6 pt-4 space-y-3">
              <!-- Search Input -->
              <div class="relative">
                <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search by name or email..."
                  class="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>

              <!-- User Type Filter -->
              <select
                v-model="userTypeFilter"
                class="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
              >
                <option v-for="type in userTypes" :key="type.value" :value="type.value">
                  {{ type.label }}
                </option>
              </select>
            </div>

            <!-- User List -->
            <div class="px-6 py-4 max-h-80 overflow-y-auto">
              <!-- Loading -->
              <div v-if="loading" class="flex items-center justify-center py-8">
                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
              </div>

              <!-- Empty State -->
              <div v-else-if="users.length === 0" class="text-center py-8 text-gray-500 text-sm">
                No users found.
              </div>

              <!-- User Rows -->
              <div v-else class="space-y-1">
                <button
                  v-for="user in users"
                  :key="user.id"
                  @click="impersonate(user)"
                  :disabled="impersonating"
                  class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-amber-50 transition-colors text-left disabled:opacity-50"
                >
                  <Avatar class="h-9 w-9 shrink-0">
                    <AvatarImage
                      :src="user.avatar_url"
                      :alt="user.name"
                    />
                    <AvatarFallback />
                  </Avatar>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-gray-900 truncate">{{ user.name }}</div>
                    <div class="text-xs text-gray-500 truncate">{{ user.email }}</div>
                  </div>
                  <span
                    class="shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                    :class="getTypeColor(user.user_type)"
                  >
                    {{ getTypeLabel(user.user_type) }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
